import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorMsg = error.message || '';
    
    // Check if it's a PDF.js rendering error we should suppress
    if (
      errorMsg.includes('Rendering cancelled') ||
      errorMsg.includes('Worker task was terminated') ||
      errorMsg.includes('sendWithPromise') ||
      errorMsg.includes('sendWithStream') ||
      errorMsg.includes('Transport destroyed') ||
      errorMsg.includes('Failed to load') ||
      errorMsg.includes('page') ||
      error.name === 'RenderingCancelledException'
    ) {
      console.log('🔇 ErrorBoundary suppressed PDF.js error');
      // Don't show error state for these
      return { hasError: false, error: null };
    }
    
    // For other errors, show error state
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if it's a PDF.js error we should suppress
    if (
      error.message?.includes('Rendering cancelled') ||
      error.message?.includes('Worker task was terminated') ||
      error.message?.includes('sendWithPromise') ||
      error.message?.includes('sendWithStream') ||
      error.message?.includes('Transport destroyed')
    ) {
      console.log('🔇 Suppressed PDF.js error in componentDidCatch');
      return; // Don't log or show
    }
    
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: 'var(--text-muted)'
        }}>
          <h2>Something went wrong</h2>
          <p>{this.state.error.message}</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

